<template>
  <v-dialog
    :value="value"
    max-width="700"
    scrollable
    @click:outside="$emit('input', false)"
  >
    <v-card>
      <v-card-title class="d-flex align-center justify-space-between px-8 pt-8">
        {{ metricName }}
      </v-card-title>

      <v-card-text class="px-8 pb-4">
        <v-skeleton-loader
          v-if="loading"
          type="list-item@4, divider, table-thead, table-row@3"
        />
        <template v-else>
          <!-- 指標設定 -->
          <v-row>
            <v-col cols="12">
              <v-text-field
                v-model="metricParams.name"
                label="名前"
              />
            </v-col>
            <v-col cols="12">
              <v-select
                v-model="metricParams.reportSectionId"
                :items="sections"
                label="セクション"
                item-text="label"
                item-value="id"
              />
            </v-col>
            <v-col cols="12">
              <v-select
                v-model="metricParams.iconKey"
                :items="iconOptions"
                item-value="key"
                label="アイコン"
                clearable
              >
                <template #item="{ item }">
                  <v-icon
                    class="mr-2"
                    :color="metricParams.iconColor || undefined"
                  >
                    {{ item.path }}
                  </v-icon>
                  {{ item.label }}
                </template>
                <template #selection="{ item }">
                  <v-icon
                    class="mr-2"
                    :color="metricParams.iconColor || undefined"
                  >
                    {{ item.path }}
                  </v-icon>
                  {{ item.label }}
                </template>
              </v-select>
            </v-col>
            <v-col cols="12">
              <div class="text-caption mb-1 grey--text">
                アイコンカラー
              </div>
              <v-menu
                v-model="colorPickerOpen"
                :close-on-content-click="false"
                offset-y
              >
                <template #activator="{ on, attrs }">
                  <div
                    class="rounded cursor-pointer"
                    :style="{
                      background: metricParams.iconColor || '#9E9E9E',
                      width: '36px',
                      height: '36px',
                      border: '2px solid rgba(255,255,255,0.2)',
                    }"
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <v-card>
                  <v-color-picker
                    v-model="metricParams.iconColor"
                    mode="hexa"
                    hide-mode-switch
                  />
                </v-card>
              </v-menu>
              <v-btn
                v-if="metricParams.iconColor"
                text
                x-small
                class="mt-1 px-0"
                @click="metricParams.iconColor = null"
              >
                クリア
              </v-btn>
            </v-col>
          </v-row>

          <v-divider class="my-6" />

          <!-- 計測項目 -->
          <div class="d-flex justify-space-between align-center mb-3">
            <div class="text-subtitle-2 font-weight-bold">
              計測項目
            </div>
            <v-btn
              color="primary"
              :ripple="false"
              small
              @click="openCreateDialog"
            >
              + 計測項目追加
            </v-btn>
          </div>

          <v-data-table
            v-sortable-data-table
            :headers="headers"
            :items="items"
            :height="items.length > 5 ? 240 : undefined"
            :fixed-header="items.length > 5"
            :footer-props="{ itemsPerPageText: '行/ページ:' }"
            no-data-text="データがありません"
            hide-default-footer
            @sorted="onSorted"
          >
            <template #[`item.drag`]>
              <v-icon
                size="18"
                class="cursor-grab"
                color="grey"
              >
                {{ icons.mdiDragVertical }}
              </v-icon>
            </template>

            <template #[`item.actions`]="{ item }">
              <v-btn
                icon
                :ripple="false"
                @click="openEditDialog(item)"
              >
                <v-icon small>
                  {{ icons.mdiPencilOutline }}
                </v-icon>
              </v-btn>
              <v-dialog max-width="400">
                <template #activator="{ on, attrs }">
                  <v-btn
                    icon
                    :ripple="false"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon small>
                      {{ icons.mdiTrashCanOutline }}
                    </v-icon>
                  </v-btn>
                </template>
                <template #default="deleteDialog">
                  <v-card>
                    <v-card-title>削除の確認</v-card-title>
                    <v-card-text>
                      この計測項目を削除しますか？この操作は取り消せません。
                    </v-card-text>
                    <v-card-actions>
                      <v-spacer />
                      <v-btn
                        text
                        small
                        @click="deleteDialog.value = false"
                      >
                        キャンセル
                      </v-btn>
                      <v-btn
                        color="error"
                        small
                        @click="destroyItem(item.id); deleteDialog.value = false"
                      >
                        削除
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </template>
              </v-dialog>
            </template>
          </v-data-table>
        </template>
      </v-card-text>

      <v-card-actions class="px-8 pb-8">
        <v-spacer />
        <v-btn
          text
          small
          @click="$emit('input', false)"
        >
          閉じる
        </v-btn>
        <v-btn
          color="primary"
          small
          :disabled="!metricParams.name"
          @click="saveMetric"
        >
          保存
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- 計測項目（FormulaItem）新規作成ダイアログ -->
    <v-dialog
      v-model="formulaItemCreateDialog"
      max-width="500"
      @click:outside="resetFormulaItemParams"
    >
      <template #default="dialog">
        <v-card>
          <v-card-title class="px-10 pt-8">
            計測項目を新規作成
          </v-card-title>
          <v-card-text class="px-10 pb-2">
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="formulaItemParams.label"
                  label="名前"
                  autofocus
                />
              </v-col>
              <v-col cols="12">
                <v-select
                  v-model="formulaItemParams.itemType"
                  :items="itemTypeOptions"
                  label="種別"
                />
              </v-col>
              <template v-if="formulaItemParams.itemType === 'context_constant'">
                <v-col cols="12">
                  <v-select
                    v-model="formulaItemParams.contextConstantKey"
                    :items="contextConstants"
                    label="定数キー"
                    item-text="label"
                    item-value="key"
                  />
                </v-col>
              </template>
              <template v-if="formulaItemParams.itemType === 'business_rule_result'">
                <v-col cols="12">
                  <v-select
                    :value="selectedBusinessRuleKey"
                    :items="groupedBusinessRules"
                    item-text="name"
                    item-value="_listKey"
                    label="ビジネスルール"
                    @change="onBusinessRuleChange"
                  >
                    <template #item="{ item }">
                      <v-list-item-content
                        v-if="item._isNameHeader"
                        class="pl-4"
                      >
                        <v-list-item-title class="text-caption grey--text">
                          {{ item.name }} ({{ item._count }})
                        </v-list-item-title>
                      </v-list-item-content>
                      <v-list-item-content
                        v-else-if="!item.header"
                        class="pl-8"
                      >
                        <v-list-item-title>{{ item.name }}</v-list-item-title>
                      </v-list-item-content>
                    </template>
                  </v-select>
                </v-col>
              </template>
              <template v-if="formulaItemParams.itemType === 'ratio'">
                <v-col cols="12">
                  <v-select
                    v-model="formulaItemParams.numeratorItemId"
                    :items="formulaItems"
                    label="分子（計測項目）"
                    item-text="attributes.label"
                    item-value="id"
                  />
                </v-col>
                <v-col cols="12">
                  <v-select
                    v-model="formulaItemParams.denominatorItemId"
                    :items="formulaItems"
                    label="分母（計測項目）"
                    item-text="attributes.label"
                    item-value="id"
                  />
                </v-col>
              </template>
            </v-row>
          </v-card-text>
          <v-card-actions class="px-10 pb-8">
            <v-spacer />
            <v-btn
              text
              small
              @click="dialog.value = false; resetFormulaItemParams()"
            >
              キャンセル
            </v-btn>
            <v-btn
              color="primary"
              small
              :disabled="!formulaItemParams.label || !formulaItemParams.itemType"
              @click="saveFormulaItem(dialog)"
            >
              作成
            </v-btn>
          </v-card-actions>
        </v-card>
      </template>
    </v-dialog>

    <!-- 計測項目追加・編集ダイアログ -->
    <v-dialog
      v-model="editDialog"
      max-width="500"
      @click:outside="resetParams"
    >
      <template #default="dialog">
        <v-card>
          <v-card-title class="d-flex align-center justify-space-between px-10 pt-8">
            {{ isNew ? '計測項目追加' : '編集' }}
            <v-btn
              v-if="isNew"
              color="secondary"
              :ripple="false"
              small
              @click="openFormulaItemCreateDialog"
            >
              + 計測項目を新規作成
            </v-btn>
          </v-card-title>
          <v-card-text class="px-10 pb-2">
            <v-row>
              <v-col cols="12">
                <v-select
                  v-model="params.reportFormulaItemId"
                  :items="formulaItems"
                  label="計測項目"
                  item-text="attributes.label"
                  item-value="id"
                />
              </v-col>
              <v-col cols="6">
                <v-select
                  v-model="params.sign"
                  :items="signOptions"
                  label="符号"
                />
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="params.coefficient"
                  label="係数"
                  type="number"
                />
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions class="px-10 pb-8">
            <v-spacer />
            <v-btn
              text
              small
              @click="closeDialog(dialog)"
            >
              キャンセル
            </v-btn>
            <v-btn
              color="primary"
              small
              :disabled="!params.reportFormulaItemId"
              @click="saveItem(dialog)"
            >
              {{ isNew ? '作成' : '更新' }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </template>
    </v-dialog>
  </v-dialog>
</template>

<script>
import {
  ref,
  reactive,
  computed,
  watch,
} from '@vue/composition-api'
import {
  mdiPencilOutline,
  mdiTrashCanOutline,
  mdiDragVertical,
  mdiTrendingUp,
  mdiCurrencyJpy,
  mdiChartBar,
  mdiChartLine,
} from '@mdi/js'
import ReportMetricItemApi from '@/api/v1/admin/ReportMetricItem'
import ReportMetricApi from '@/api/v1/admin/ReportMetric'
import ReportFormulaItemApi from '@/api/v1/admin/ReportFormulaItem'
import sortableDataTable from '@/directives/sortableDataTable'

const itemTypeOptions = [
  { text: 'コンテキスト定数', value: 'context_constant' },
  { text: 'ビジネスルール結果', value: 'business_rule_result' },
  { text: '比率', value: 'ratio' },
]

const iconOptions = [
  { key: 'mdiChartLine', path: mdiChartLine, label: '折れ線グラフ（デフォルト）' },
  { key: 'mdiTrendingUp', path: mdiTrendingUp, label: 'トレンド上昇' },
  { key: 'mdiCurrencyJpy', path: mdiCurrencyJpy, label: '円マーク' },
  { key: 'mdiChartBar', path: mdiChartBar, label: '棒グラフ' },
]

export default {
  directives: { sortableDataTable },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    metricId: {
      type: [String, Number],
      default: null,
    },
    metricName: {
      type: String,
      default: '',
    },
  },
  setup(props, { emit }) {
    const items = ref([])
    const formulaItems = ref([])
    const sections = ref([])
    const businessRules = ref([])
    const contextConstants = ref([])
    const loading = ref(false)
    const editDialog = ref(false)
    const formulaItemCreateDialog = ref(false)
    const colorPickerOpen = ref(false)

    const metricParams = reactive({
      name: '',
      reportSectionId: null,
      iconKey: null,
      iconColor: null,
    })
    const params = reactive({
      id: null,
      reportFormulaItemId: null,
      sign: '+',
      coefficient: '1.0',
    })
    const formulaItemParams = reactive({
      label: '',
      itemType: null,
      contextConstantKey: null,
      businessRuleId: null,
      numeratorItemId: null,
      denominatorItemId: null,
    })

    const groupedBusinessRules = computed(() => {
      const typeGroups = {}
      businessRules.value.forEach(br => {
        const typeKey = br.aggregationLabel || 'その他'
        const nameKey = br.aggregationName || '不明'
        if (!typeGroups[typeKey]) typeGroups[typeKey] = {}
        if (!typeGroups[typeKey][nameKey]) typeGroups[typeKey][nameKey] = []
        typeGroups[typeKey][nameKey].push(br)
      })

      return Object.entries(typeGroups).flatMap(([typeLabel, nameGroups]) => [
        { header: typeLabel },
        ...Object.entries(nameGroups).flatMap(([nameLabel, rules]) => [
          {
            name: nameLabel, _isNameHeader: true, disabled: true, id: `__h_${typeLabel}_${nameLabel}`, _listKey: `__h_${typeLabel}_${nameLabel}`, _count: rules.length,
          },
          ...rules.map(br => ({ ...br, _listKey: `${br.id}_${typeLabel}_${nameLabel}` })),
        ]),
      ])
    })

    const selectedBusinessRuleKey = computed(() => {
      if (!formulaItemParams.businessRuleId) return null
      const found = groupedBusinessRules.value.find(
        item => !item._isNameHeader && !item.header && item.id === formulaItemParams.businessRuleId,
      )

      return found ? found._listKey : null
    })

    const onBusinessRuleChange = val => {
      const found = groupedBusinessRules.value.find(item => item._listKey === val)
      formulaItemParams.businessRuleId = found ? found.id : null
    }

    const isNew = computed(() => params.id === null)

    const signOptions = [
      { text: '+', value: '+' },
      { text: '-', value: '-' },
    ]

    const headers = [
      {
        text: '', value: 'drag', sortable: false, width: '40px',
      },
      { text: '計測項目', value: 'attributes.formulaItemLabel' },
      { text: '符号', value: 'attributes.sign' },
      { text: '係数', value: 'attributes.coefficient' },
      {
        text: '', value: 'actions', sortable: false, align: 'end',
      },
    ]

    const fetchMetricItems = async () => {
      if (!props.metricId) return
      const res = await ReportMetricItemApi.getList(props.metricId)
      items.value = res.data.reportMetricItems?.data || []
    }

    const fetchFormulaItems = async () => {
      const res = await ReportFormulaItemApi.getList()
      formulaItems.value = res.data.reportFormulaItems?.data || []
      businessRules.value = res.data.meta?.businessRules || []
      contextConstants.value = res.data.meta?.contextConstants || []
    }

    const fetchMetric = async () => {
      const res = await ReportMetricApi.getList()
      const allMetrics = res.data.reportMetrics?.data || []
      sections.value = res.data.meta?.sections || []
      const metric = allMetrics.find(m => String(m.id) === String(props.metricId))
      if (metric) {
        metricParams.name = metric.attributes.name
        metricParams.reportSectionId = metric.attributes.reportSectionId || null
        metricParams.iconKey = metric.attributes.iconKey || null
        metricParams.iconColor = metric.attributes.iconColor || null
      }
    }

    const fetchItems = async () => {
      if (!props.metricId) return
      loading.value = true
      try {
        await Promise.all([fetchMetricItems(), fetchFormulaItems(), fetchMetric()])
      } catch (e) {
        // TODO
      } finally {
        loading.value = false
      }
    }

    watch(() => props.value, val => {
      if (val && props.metricId) fetchItems()
    })

    const saveMetric = async () => {
      try {
        await ReportMetricApi.update(props.metricId, {
          name: metricParams.name,
          report_section_id: metricParams.reportSectionId,
          icon_key: metricParams.iconKey,
          icon_color: metricParams.iconColor,
        })
        emit('input', false)
      } catch (e) {
        // TODO
      }
    }

    const resetParams = () => {
      params.id = null
      params.reportFormulaItemId = null
      params.sign = '+'
      params.coefficient = '1.0'
    }

    const openCreateDialog = () => {
      resetParams()
      editDialog.value = true
    }

    const resetFormulaItemParams = () => {
      formulaItemParams.label = ''
      formulaItemParams.itemType = null
      formulaItemParams.contextConstantKey = null
      formulaItemParams.businessRuleId = null
      formulaItemParams.numeratorItemId = null
      formulaItemParams.denominatorItemId = null
    }

    const openFormulaItemCreateDialog = () => {
      resetFormulaItemParams()
      formulaItemCreateDialog.value = true
    }

    const saveFormulaItem = async dialog => {
      try {
        await ReportFormulaItemApi.create({
          label: formulaItemParams.label,
          item_type: formulaItemParams.itemType,
          context_constant_key: formulaItemParams.contextConstantKey,
          business_rule_id: formulaItemParams.businessRuleId,
          numerator_item_id: formulaItemParams.numeratorItemId,
          denominator_item_id: formulaItemParams.denominatorItemId,
        })
        // eslint-disable-next-line no-param-reassign
        dialog.value = false
        resetFormulaItemParams()
        await fetchFormulaItems()
      } catch (e) {
        // TODO
      }
    }

    const openEditDialog = item => {
      params.id = item.id
      params.reportFormulaItemId = item.attributes.reportFormulaItemId
      params.sign = item.attributes.sign
      params.coefficient = item.attributes.coefficient
      editDialog.value = true
    }

    const closeDialog = dialog => {
      // eslint-disable-next-line no-param-reassign
      dialog.value = false
      resetParams()
    }

    const saveItem = async dialog => {
      try {
        const payload = {
          report_formula_item_id: params.reportFormulaItemId,
          sign: params.sign,
          coefficient: params.coefficient,
        }
        if (isNew.value) {
          await ReportMetricItemApi.create(props.metricId, payload)
        } else {
          await ReportMetricItemApi.update(props.metricId, params.id, payload)
        }
        closeDialog(dialog)
        await fetchMetricItems()
      } catch (e) {
        // TODO
      }
    }

    const destroyItem = async id => {
      try {
        await ReportMetricItemApi.destroy(props.metricId, id)
        fetchItems()
      } catch (e) {
        // TODO
      }
    }

    const onSorted = async event => {
      const { oldIndex, newIndex } = event
      if (oldIndex === newIndex) return

      const moved = items.value.splice(oldIndex, 1)[0]
      items.value.splice(newIndex, 0, moved)

      const positions = items.value.map((item, idx) => ({ id: item.id, position: idx }))
      try {
        await ReportMetricItemApi.updatePositions(props.metricId, { positions })
      } catch (e) {
        // TODO
      }
    }

    return {
      items,
      formulaItems,
      sections,
      businessRules,
      contextConstants,
      groupedBusinessRules,
      selectedBusinessRuleKey,
      onBusinessRuleChange,
      loading,
      editDialog,
      formulaItemCreateDialog,
      colorPickerOpen,
      metricParams,
      params,
      formulaItemParams,
      isNew,
      headers,
      signOptions,
      itemTypeOptions,
      iconOptions,
      openCreateDialog,
      openEditDialog,
      closeDialog,
      saveItem,
      saveMetric,
      destroyItem,
      resetParams,
      onSorted,
      openFormulaItemCreateDialog,
      resetFormulaItemParams,
      saveFormulaItem,
      icons: {
        mdiPencilOutline,
        mdiTrashCanOutline,
        mdiDragVertical,
      },
    }
  },
}
</script>
